/**
 * Push Notification Utilities
 * 
 * Helper functions to send push notifications via OneSignal
 * when notifications are created in the database.
 */

/**
 * Send a push notification to a user via OneSignal
 * 
 * @param userId - The internal users.id (not auth_id)
 * @param title - Notification title
 * @param message - Notification message
 * @param actionUrl - Optional URL to navigate to when clicked
 * @param data - Optional additional data to pass with notification
 * @param type - Optional notification type for filtering based on user settings
 */
export async function sendPushNotification(
  userId: string,
  title: string,
  message: string,
  actionUrl?: string,
  data?: Record<string, any>,
  type?: string
): Promise<void> {
  // Only send push notifications if OneSignal is configured
  if (!process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID || !process.env.ONESIGNAL_REST_API_KEY) {
    // Silently fail if OneSignal is not configured
    return
  }

  try {
    // Call the API endpoint to send push notification
    const response = await fetch("/api/notifications/push", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId,
        title,
        message,
        actionUrl,
        data,
        type
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      console.error("Failed to send push notification:", error)
    }
  } catch (error) {
    // Don't throw - push notifications are optional
    // Log error but don't break the main flow
    console.error("Error sending push notification:", error)
  }
}

/**
 * Create a database notification and send push notification
 * This is a convenience function that does both operations
 * 
 * For client-side usage, this now calls a server-side API route to ensure
 * notifications are created reliably using service client (bypasses RLS)
 * 
 * @param supabase - Supabase client instance (used to get access token)
 * @param notificationData - Notification data to insert
 * @param sendPush - Whether to also send push notification (default: true)
 */
export async function createNotificationWithPush(
  supabase: any,
  notificationData: {
    user_id: string
    type: string
    title: string
    message: string
    action_url?: string
    action_label?: string
    related_id?: string
  },
  sendPush: boolean = true
): Promise<{ notificationId?: string; error?: any }> {
  try {
    // Get access token from Supabase session for authentication
    let accessToken: string | null = null
    try {
      const { data: { session } } = await supabase.auth.getSession()
      accessToken = session?.access_token || null
      
      // Fallback: try to get token from localStorage if session is not available
      if (!accessToken && typeof window !== 'undefined') {
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
        const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
        const projectRef = match ? match[1] : 'default'
        const storageKey = `sb-${projectRef}-auth-token`
        const storedSession = localStorage.getItem(storageKey)
        if (storedSession) {
          const parsedSession = JSON.parse(storedSession)
          accessToken = parsedSession?.access_token || parsedSession?.currentSession?.access_token || null
        }
      }
    } catch (tokenError) {
      console.warn("Failed to get access token:", tokenError)
      // Continue without token - cookies might be enough
    }

    // Build headers - include Authorization if we have a token
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`
    }

    // Use server-side API route for reliable notification creation
    // This ensures notifications are created using service client (bypasses RLS)
    const response = await fetch("/api/notifications/create", {
      method: "POST",
      headers,
      credentials: "include", // Include cookies for authentication
      body: JSON.stringify({
        ...notificationData,
        send_push: sendPush,
      }),
    })

    const result = await response.json()

    if (!response.ok || !result.success) {
      const error = result.error || "Failed to create notification"
      console.error("Error creating notification via API:", {
        error,
        details: result.details,
        notificationData
      })
      return { error: { message: error, details: result.details } }
    }

    return { notificationId: result.notificationId }
  } catch (error: any) {
    console.error("Error calling notification API:", {
      message: error.message,
      error
    })
    return { error }
  }
}


